import * as React from "react";
import { graphql, compose } from "react-apollo";
import gql from "graphql-tag";
import BOTable, {
  BOColumns,
  BOColumn,
  BOBody,
  BORow,
  BOCell,
  BOHeader
} from "app/components/BOComponents/BOTable/BOTable";
import {
  formatDate,
  formatStatusColour,
  formatSecure3d,
  SnapminLabel
} from "app/utils/filters";

type State = {
  bankCardChange: any;
  loading: boolean;
};

class CardLoadSummary extends React.Component<any, State> {
  constructor(props: any) {
    super(props);

    this.state = {
      bankCardChange: null,
      loading: true
    };
  }

  componentDidMount() {
    this.runQuery(this.props.match.params.id);
  }

  componentWillReceiveProps(nextProps: any) {
    if (this.props.match.params.id !== nextProps.match.params.id) {
      this.runQuery(nextProps.match.params.id);
    }
  }

  runQuery = (id: string): void => {
    this.setState({ loading: true });
    this.props.bankCardChanges
      .refetch()
      .then(({ data: { bank_card_changes } }): void => {
        this.setState({
          loading: false,
          bankCardChange: bank_card_changes[0]
        });
        this.props.setHeader({
          title: "Ops > Card Load",
          description: "SnapScan card loads"
        });
      })
      .catch(error => console.error(error));
  };

  render() {
    const { bankCardChange, loading } = this.state;

    const Unknown = () => {
      return (
        <span style={{ color: "darkorange", marginLeft: "5px" }}>Unknown</span>
      );
    };

    const cardLoadSummaryTable = {
      columns: (): JSX.Element => {
        return (
          <BOColumns>
            <BOColumn>Date</BOColumn>
            <BOColumn>Bin</BOColumn>
            <BOColumn>Secure 3D</BOColumn>
            <BOColumn>Country code</BOColumn>
            <BOColumn>User</BOColumn>
            <BOColumn>Message</BOColumn>
          </BOColumns>
        );
      },
      body: (): JSX.Element => {
        return (
          <BOBody>
            {bankCardChange ? (
              <BORow rowColor={formatStatusColour(bankCardChange.status)}>
                <BOCell>{formatDate(bankCardChange.created_at)}</BOCell>
                <BOCell>{bankCardChange.bin}</BOCell>
                <BOCell>
                  {formatSecure3d(bankCardChange.secure3d_requested)}
                </BOCell>
                <BOCell>{bankCardChange.country_code}</BOCell>
                <BOCell>
                  <SnapminLabel
                    link={`/snapscan/ops/users/${
                      bankCardChange.device.user.external_reference
                    }/payments`}
                    snapminLink={bankCardChange.device.user.snapmin_url}
                  >
                    {bankCardChange.device.user.email}
                  </SnapminLabel>
                </BOCell>
                <BOCell>{bankCardChange.message}</BOCell>
              </BORow>
            ) : (
              <BORow>
                <BOCell colSpan="6" align="center">
                  Data not available
                </BOCell>
              </BORow>
            )}
          </BOBody>
        );
      }
    };

    const payservBasicTable = {
      header: (): JSX.Element => {
        return <BOHeader title="Basic" />;
      },
      body: (): JSX.Element => {
        return (
          <BOBody>
            <BORow>
              <BOCell>
                <b>Date</b>
              </BOCell>
              <BOCell>{bankCardChange.payserv_payment.created_at}</BOCell>

              <BOCell>
                <b>Bank response code</b>
              </BOCell>
              <BOCell>
                {bankCardChange.payserv_payment.bank_response_code}
              </BOCell>
            </BORow>
            <BORow>
              <BOCell>
                <b>Reference</b>
              </BOCell>
              <BOCell>{bankCardChange.payserv_payment.type}</BOCell>

              <BOCell>
                <b>Message</b>
              </BOCell>
              <BOCell>{bankCardChange.payserv_payment.message}</BOCell>
            </BORow>
            <BORow>
              <BOCell>
                <b>Reserve amount</b>
              </BOCell>
              <BOCell>{bankCardChange.payserv_payment.reserve_amount}</BOCell>

              <BOCell>
                <b>Finalize amount</b>
              </BOCell>
              <BOCell>
                {bankCardChange.payserv_payment.finalize_amount ? (
                  bankCardChange.payserv_payment.finalize_amount
                ) : (
                  <Unknown />
                )}
              </BOCell>
            </BORow>
          </BOBody>
        );
      }
    };

    const payservDetailsTable = {
      header: (): JSX.Element => {
        return <BOHeader title="Details" />;
      },
      body: (): JSX.Element => {
        return (
          <BOBody>
            <BORow>
              <BOCell>
                <b>Card acceptor name location</b>
              </BOCell>
              <BOCell>
                {bankCardChange.payserv_payment.card_acceptor_name_location}
              </BOCell>
              <BOCell>
                <b>External reference</b>
              </BOCell>
              <BOCell>
                {bankCardChange.payserv_payment.external_reference}
              </BOCell>
            </BORow>
            <BORow>
              <BOCell>
                <b>Recon identifier</b>
              </BOCell>
              <BOCell>{bankCardChange.payserv_payment.recon_identifier}</BOCell>
              <BOCell>
                <b>Retrieval reference number</b>
              </BOCell>
              <BOCell>
                {bankCardChange.payserv_payment.retrieval_reference_number}
              </BOCell>
            </BORow>
            <BORow>
              <BOCell>
                <b>Config</b>
              </BOCell>
              <BOCell colSpan={3}>
                {bankCardChange.payserv_payment.config}
              </BOCell>
            </BORow>
          </BOBody>
        );
      }
    };

    const payservErrorTable = {
      header: (): JSX.Element => {
        return <BOHeader title="Error" />;
      },
      columns: (): JSX.Element => {
        return (
          <BOColumns>
            <BOColumn>Reason</BOColumn>
            <BOColumn>Messsage</BOColumn>
          </BOColumns>
        );
      },
      body: (): JSX.Element => {
        return (
          <BOBody>
            <BORow rowColor="#ffebeb">
              <BOCell>{bankCardChange.payserv_payment.error_reason}</BOCell>
              <BOCell>{bankCardChange.payserv_payment.error_message}</BOCell>
            </BORow>
          </BOBody>
        );
      }
    };

    return (
      <section className="padded">
        {loading ? (
          <div>loading...</div>
        ) : (
          <span>
            <BOTable
              name="card_loads_summary_table"
              columns={cardLoadSummaryTable.columns()}
              body={cardLoadSummaryTable.body()}
              loading={loading}
            />
            {bankCardChange.payserv_payment ? (
              <span>
                <BOTable
                  name="card_loads_payserv_basic_table"
                  header={payservBasicTable.header()}
                  body={payservBasicTable.body()}
                  loading={loading}
                />

                <BOTable
                  name="card_loads_payserv_details_table"
                  header={payservDetailsTable.header()}
                  body={payservDetailsTable.body()}
                  loading={loading}
                />
                {bankCardChange.payserv_payment.error_reason ? (
                  <BOTable
                    name="card_loads_payserv_error_table"
                    header={payservErrorTable.header()}
                    columns={payservErrorTable.columns()}
                    body={payservErrorTable.body()}
                    loading={loading}
                  />
                ) : null}
              </span>
            ) : null}
          </span>
        )}
      </section>
    );
  }
}

const bankCardChangesQuery = graphql(
  gql`
    query bankCardChanges($external_reference: ID) {
      bank_card_changes(external_reference: $external_reference) {
        created_at
        snapmin_url
        bin
        status
        secure3d_requested
        country_code
        message
        device {
          user {
            external_reference
            email
          }
        }
        payserv_payment {
          bank_response_code
          card_acceptor_name_location
          config
          created_at
          error_message
          error_reason
          external_reference
          finalize_amount
          message
          psp
          psp_reference
          recon_identifier
          refund_amount
          reserve_amount
          retrieval_reference_number
          strategy
          terminal_identifier
          type
        }
      }
    }
  `,
  {
    name: "bankCardChanges",
    options({
      match: {
        params: { id }
      }
    }: any) {
      return {
        variables: {
          external_reference: id
        }
      };
    }
  }
);

export default compose(bankCardChangesQuery)(CardLoadSummary);
